import { frameId, type CompiledChunk, type FrameRect } from "daedalus-core";
import { parseHarvestMark, type ToolKind } from "odyssey-shared";
import { chunkKey } from "../chunk-key.ts";

/**
 * The harvestable props of the booted map as the page knows them (ADR-142 decision 3.1): every client chunk lists the
 * props the compile marked (`ChunkMark`), each with its index among the map's marked props, so the page reads the
 * indices the server's frames name. Built once when the world loads, from chunks the page already holds.
 */

/**
 * One harvestable prop: its index on the map, its authored id (which the prop views draw it by), the tool it needs in
 * hand, the chunk that holds it, its feet point in sixteenths of a tile and the box its art fills in world pixels.
 */
export interface PageMark {
  readonly n: number;
  readonly id: string;
  readonly tool: ToolKind;
  readonly cx: number;
  readonly cy: number;
  readonly x: number;
  readonly y: number;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The map's harvestable props, by index and by chunk.
 */
export interface HarvestMarks {
  readonly byIndex: ReadonlyMap<number, PageMark>;
  readonly byChunk: ReadonlyMap<string, readonly PageMark[]>;
}

/**
 * Reads the marks of a map's chunks. A mark that is no harvest's, or whose prop the chunk does not hold, is left out.
 *
 * @param chunks The map's client chunks by `cx,cy`.
 * @param frames The asset manifest's frame rects, for each prop's art box.
 * @param tileSize Tile size in pixels.
 * @returns The marks, empty for a map with no harvestable prop.
 */
export function readHarvestMarks(chunks: ReadonlyMap<string, CompiledChunk>, frames: Readonly<Record<string, FrameRect>>, tileSize: number): HarvestMarks {
  const byIndex = new Map<number, PageMark>();
  const byChunk = new Map<string, PageMark[]>();
  for (const chunk of chunks.values()) {
    if (chunk.marks === undefined) {
      continue;
    }
    const props = new Map<string, { readonly tileset: string; readonly tile: number }>();
    for (const layer of chunk.layers) {
      if (layer.kind === "prop") {
        for (const prop of layer.props) {
          props.set(prop.id, prop.frame);
        }
      }
    }
    for (const mark of chunk.marks) {
      const read = parseHarvestMark(mark.mark);
      const frame = props.get(mark.id);
      const rect = frame === undefined ? undefined : frames[frameId(frame.tileset, null, frame.tile)];
      if (read === null || rect === undefined) {
        continue;
      }
      const bottom = (mark.y * tileSize) / 16;
      const left = (mark.x * tileSize) / 16 - rect.w / 2;
      const entry: PageMark = { n: mark.n, id: mark.id, tool: read.tool, cx: chunk.cx, cy: chunk.cy, x: mark.x, y: mark.y, left, top: bottom - rect.h, right: left + rect.w, bottom };
      byIndex.set(mark.n, entry);
      const key = chunkKey(chunk.cx, chunk.cy);
      const list = byChunk.get(key);
      if (list === undefined) {
        byChunk.set(key, [entry]);
      } else {
        list.push(entry);
      }
    }
  }
  return { byIndex, byChunk };
}
